import axios from "axios";
import { TWO_HOURS } from "../constants";
import { Course } from "../types";
import { retrieveCourseData, storeCourseData } from "../utils/localstorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL!!;
const ALLOWED_ORIGIN = new URL(BACKEND_URL).origin;

function validateUrl(url: string): void {
  if (new URL(url).origin !== ALLOWED_ORIGIN) {
    throw new Error(
      `Blocked request to untrusted origin: ${new URL(url).origin}`
    );
  }
}

/**
 * Normalize response data to Course[].
 * During migration the backend emits the legacy shape from versioned S3 data,
 * so this should just validate that we have an array.
 * Handles potential future switch to v2 payload.
 */
function normalizeCourses(data: unknown): Course[] {
  if (Array.isArray(data)) {
    return data as Course[];
  }

  // Future-proof: handle versioned document if backend sends it directly
  if (
    data &&
    typeof data === "object" &&
    "schemaVersion" in data &&
    "courses" in data
  ) {
    const doc = data as { schemaVersion: number; courses: any[] };
    // Map v2 normalized courses to legacy shape
    return doc.courses.map((c: any) => ({
      TapahtumaID: c.id || "",
      Nimi: c.name || "",
      Ajankohta: c.dateDisplay || "",
      PostitoimipaikkaListassa: c.location || "",
      TilaListassa: c.status || "",
      JarjestavaYksikko: c.organizer || "",
      TutustuTarkemminLinkki: c.sourceUrl || "",
      Sijaintikunta: c.location || "",
      SijaintikuntaListassa: c.location || "",
    })) as Course[];
  }

  return [];
}

export const getCourses = (): Promise<Course[]> => {
  const cachedData = retrieveCourseData();

  if (!cachedData || cachedData.timestamp < Date.now() - TWO_HOURS) {
    validateUrl(BACKEND_URL);
    return axios
      .get(BACKEND_URL, {
        headers: {
          Accept: "application/json",
        },
      })
      .then((response) => {
        const data = normalizeCourses(response.data);
        storeCourseData(data);
        return data;
      });
  } else {
    return cachedData.data;
  }
};

export const getCourseDescription = (courseId: string): Promise<string> => {
  // The courseId is an opaque string that is already URL-safe — do not re-encode
  const url = `${BACKEND_URL}description/${courseId}`;
  validateUrl(url);
  return axios
    .get(url, {
      headers: {
        Accept: "text/html",
      },
    })
    .then((response) => response.data as string);
};
